import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import {
  upsertZoneRouter,
  type RouterRecord,
  type RouterUpsert,
} from '@/api/zoneRouter';
import {
  ROUTER_MODEL_DROPDOWN_VALUES,
  ROUTER_MODEL_DROPDOWN_LABEL_KEYS,
  pickRouterModelDropdownValue,
  resolveRouterModelName,
  type RouterModelDropdownValue,
} from './routerModelOptions';
import { extractApiError } from '@/utils/apiError';
import { Tooltip } from '@/components/ui/tooltip';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zoneId: number | string;
  initial: RouterRecord;
  /**
   * When the operator already tried Save once and the post-save probe
   * failed, the parent re-opens the dialog with this flag set so the
   * Advanced section is forced open + the inline message is rendered.
   */
  forceAdvancedOpen?: boolean;
  /**
   * Fires after a successful PUT. The parent kicks off a probe
   * immediately — the modal closes right after Save and the probe runs
   * in the background.
   */
  onSaved: () => void;
}

/**
 * Router edit modal.
 *
 * Visual polish pass (2026-06-02): the disclosure animates open with
 * a grid-rows-fr trick (no max-h jankiness), the probe-populated
 * readonly chips look like actual chips (rounded bordered tags grouped
 * under an "auto-detected" header), and the form rhythm is unified —
 * every label sits on top with the same caption face, every input row
 * carries the same 12px vertical gap.
 *
 * Layout:
 *
 *   Basic (always visible)
 *     Адрес админки (full row), Логин + Пароль (2-up)
 *
 *   Расширенные настройки (collapsed by default; forced open after a
 *   failed post-save probe — orange warning banner appears alongside)
 *     Editable: Модель override + free-text, IP в LAN, WAN IP,
 *               Дополнительный адрес админки, Заметки.
 *     Read-only chips block: Полная модель (SKU), Прошивка,
 *               Заводская прошивка, Сетевые интерфейсы (count +
 *               JSON expander).
 */
export default function RouterEditDialog({
  open,
  onOpenChange,
  zoneId,
  initial,
  forceAdvancedOpen = false,
  onSaved,
}: Props) {
  const { t } = useT();

  // Basic fields
  const [adminUrl, setAdminUrl] = useState(initial.admin_url || '');
  const [username, setUsername] = useState(initial.username || '');
  const [password, setPassword] = useState(initial.password || '');

  // Advanced editable fields
  const [modelDropdown, setModelDropdown] = useState<RouterModelDropdownValue>(
    pickRouterModelDropdownValue(initial.model_name || ''),
  );
  const [modelOtherText, setModelOtherText] = useState(
    pickRouterModelDropdownValue(initial.model_name || '') === 'other'
      ? initial.model_name || ''
      : '',
  );
  const [gatewayIp, setGatewayIp] = useState(initial.gateway_ip || '');
  const [wanIp, setWanIp] = useState(initial.wan_ip || '');
  const [remoteAccessUrl, setRemoteAccessUrl] = useState(initial.remote_access_url || '');
  const [notes, setNotes] = useState(initial.notes || '');

  const [advancedOpen, setAdvancedOpen] = useState(forceAdvancedOpen);
  const [bridgeJsonOpen, setBridgeJsonOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync from `initial` every time the modal is opened — covers the
  // "Save, probe-fails, re-open" cycle so the operator sees the values
  // they just typed (now persisted) instead of stale state.
  useEffect(() => {
    if (!open) return;
    setAdminUrl(initial.admin_url || '');
    setUsername(initial.username || '');
    setPassword(initial.password || '');
    const picked = pickRouterModelDropdownValue(initial.model_name || '');
    setModelDropdown(picked);
    setModelOtherText(picked === 'other' ? initial.model_name || '' : '');
    setGatewayIp(initial.gateway_ip || '');
    setWanIp(initial.wan_ip || '');
    setRemoteAccessUrl(initial.remote_access_url || '');
    setNotes(initial.notes || '');
    setAdvancedOpen(forceAdvancedOpen);
    setBridgeJsonOpen(false);
    setError(null);
  }, [open, initial, forceAdvancedOpen]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const payload: RouterUpsert = {
      model_name: resolveRouterModelName(modelDropdown, modelOtherText),
      gateway_ip: gatewayIp.trim() || null,
      wan_ip: wanIp.trim() || null,
      admin_url: adminUrl.trim(),
      // remote_access_url is the *alternate* URL — operator-typed,
      // distinct from admin_url. We don't pre-fill from admin_url.
      remote_access_url: remoteAccessUrl.trim(),
      username,
      password,
      notes,
    };
    try {
      await upsertZoneRouter(zoneId, payload);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      if (isAxiosError(e)) {
        setError(extractApiError(e, t('error.general')));
      } else {
        setError(t('error.general'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const bridgeLayoutCount = Array.isArray(initial.bridge_layout)
    ? (initial.bridge_layout as unknown[]).length
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="relative max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('equipment.router.editDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {forceAdvancedOpen && (
            <div className="rounded-lg border border-[color:var(--color-warning)]/30 bg-[color:var(--color-warning)]/10 px-3 py-2 text-xs leading-relaxed text-[color:var(--color-warning)]">
              {t('equipment.router.editDialog.probeFailed')}
            </div>
          )}

          {/* Basic section */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="re-admin">{t('equipment.router.adminUrl')}</Label>
              <Input
                id="re-admin"
                value={adminUrl}
                onChange={e => setAdminUrl(e.target.value)}
                placeholder="http://192.168.1.1 or https://my.keenetic.net/…"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="re-user">{t('equipment.router.username')}</Label>
                <Input
                  id="re-user"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="font-mono"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="re-pw">{t('equipment.router.password')}</Label>
                <Input
                  id="re-pw"
                  name="router-secret"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="font-mono"
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          {/* Advanced section — collapsible */}
          <div className="border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setAdvancedOpen(v => !v)}
              className="flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
              aria-expanded={advancedOpen}
              data-testid="router-edit-advanced-toggle"
            >
              <ChevronRight
                className={cn(
                  'size-3.5 transition-transform duration-150',
                  advancedOpen && 'rotate-90',
                )}
              />
              {t('equipment.router.editDialog.advanced')}
            </button>
            <div
              className={cn(
                'grid transition-all duration-200 ease-out',
                advancedOpen ? 'mt-3 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0',
              )}
            >
              <div className="overflow-hidden">
                <div className="space-y-3">
                  {/* Editable */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="re-model">{t('equipment.router.modelName')}</Label>
                      <Select
                        id="re-model"
                        value={modelDropdown}
                        onChange={e => setModelDropdown(e.target.value as RouterModelDropdownValue)}
                      >
                        {ROUTER_MODEL_DROPDOWN_VALUES.map(v => (
                          <option key={v} value={v}>{t(ROUTER_MODEL_DROPDOWN_LABEL_KEYS[v])}</option>
                        ))}
                      </Select>
                      {modelDropdown === 'other' && (
                        <Input
                          id="re-model-other"
                          value={modelOtherText}
                          onChange={e => setModelOtherText(e.target.value)}
                          placeholder={t('equipment.router.modelName.otherPlaceholder')}
                        />
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="re-gw">{t('equipment.router.gatewayIp')}</Label>
                      <Input
                        id="re-gw"
                        value={gatewayIp}
                        onChange={e => setGatewayIp(e.target.value)}
                        placeholder="192.168.1.1"
                        className="font-mono tabular-nums"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="re-wan">{t('equipment.router.wanIp')}</Label>
                      <Input
                        id="re-wan"
                        value={wanIp}
                        onChange={e => setWanIp(e.target.value)}
                        placeholder="203.0.113.42"
                        className="font-mono tabular-nums"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="re-remote">
                        {t('equipment.router.remoteAccessUrl')}
                      </Label>
                      <Input
                        id="re-remote"
                        value={remoteAccessUrl}
                        onChange={e => setRemoteAccessUrl(e.target.value)}
                        placeholder="https://keenetic-XXXX.keenetic.link"
                      />
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        {t('equipment.router.remoteAccessUrlHelp')}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="re-notes">{t('equipment.router.notes')}</Label>
                    <textarea
                      id="re-notes"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      rows={3}
                      className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
                    />
                  </div>

                  {/* Read-only probe-populated chips */}
                  <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      <span className="size-1.5 rounded-full bg-[color:var(--color-success)]" aria-hidden />
                      {t('equipment.router.editDialog.detected')}
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <ReadonlyChip
                        label={t('equipment.router.modelFullSku')}
                        value={initial.model || ''}
                      />
                      <ReadonlyChip
                        label={t('equipment.router.firmware')}
                        value={initial.firmware_version || ''}
                        mono
                      />
                      <ReadonlyChip
                        label={t('equipment.router.firmwareFactory')}
                        value={initial.factory_firmware_version || ''}
                        mono
                      />
                      <ReadonlyChip
                        label={t('equipment.router.bridgeLayout')}
                        value={
                          bridgeLayoutCount > 0
                            ? t('equipment.router.editDialog.bridgeLayoutCount').replace(
                                '{{count}}',
                                String(bridgeLayoutCount),
                              )
                            : ''
                        }
                        trailing={
                          bridgeLayoutCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => setBridgeJsonOpen(v => !v)}
                              className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-primary transition-colors hover:bg-primary-muted"
                            >
                              {t('equipment.router.editDialog.bridgeLayoutShow')}
                            </button>
                          ) : null
                        }
                      />
                    </div>
                    {bridgeJsonOpen && bridgeLayoutCount > 0 && (
                      <pre className="mt-1 max-h-48 overflow-auto rounded-md border border-border bg-background/80 p-2 text-[10px] leading-relaxed">
                        {JSON.stringify(initial.bridge_layout, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="size-3.5 animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Compact two-line chip for the "detected automatically" panel.
 * Label (caption) on top, value on bottom — matches the StatTile face
 * used in the Router card's Сводка block so the modal feels like an
 * extension of the same surface, not a different aesthetic.
 */
function ReadonlyChip({
  label,
  value,
  trailing = null,
  mono = false,
}: {
  label: string;
  value: string;
  trailing?: React.ReactNode;
  mono?: boolean;
}) {
  const { t } = useT();
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-card/60 px-2.5 py-1.5">
      <div className="min-w-0 space-y-0.5">
        <div className="truncate text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
          {label}
        </div>
        <Tooltip content={value}><div
          className={cn(
            'min-w-0 truncate text-xs leading-tight',
            mono && 'font-mono tabular-nums',
          )}
        >
          {value || t('equipment.router.dash')}
        </div></Tooltip>
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
    </div>
  );
}
